import { ActionContext, ActionTree } from 'vuex';

import { RootState } from '@/modules/ewerp/store';
import { useEwerpCommonStore } from '@/modules/ewerp/store/ewerpCommonStore';

import { State } from './state';

import { Mutations, MutationTypes } from './mutations';

import { Produktionsstatus, Maschinenplanung } from '@/modules/ewerp/models/ProduktionsstatusModels';
import LagerService from '@/modules/ewerp/service/LagerService';

export enum ActionTypes {
  GetProduktionsstatus = 'GETPRODUKTIONSSTATUS',
  UpdateGetProduktionsstatus = 'UPDATEGETPRODUKTIONSSTATUS',
  getMaschinenPlanungHeute = 'GETMASCHINENPLANUNGHEUTE',
  updateMaschinenPlanungHeute = 'UPDATEMASCHINENPLANUNGHEUTE',
}

type ActionArguments = Omit<ActionContext<State, RootState>, 'commit'> & {
  commit<K extends keyof Mutations>(key: K, payload: Parameters<Mutations[K]>[1]): ReturnType<Mutations[K]>;
};

export type Actions = {
  [ActionTypes.GetProduktionsstatus](context: ActionArguments): Promise<void>;
  [ActionTypes.UpdateGetProduktionsstatus](context: ActionArguments): Promise<void>;
  [ActionTypes.getMaschinenPlanungHeute](context: ActionArguments): Promise<void>;
  [ActionTypes.updateMaschinenPlanungHeute](context: ActionArguments): Promise<void>;
};

export const actions: ActionTree<State, RootState> & Actions = {
  async [ActionTypes.GetProduktionsstatus]({ commit }: ActionArguments) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.produktionsstatusLoading = true;
    commonStore.setStatus(status);

    return LagerService.getProduktionsstatus()
      .then((res) => {
        status.produktionsstatusLoading = false;
        status.produktionsstatusFetched = true;
        commonStore.setStatus(status);

        const result: Array<Produktionsstatus> = res.data;
        commit(MutationTypes.GetProduktionsstatusSuccess, result);
      })
      .catch((error) => {
        status.produktionsstatusLoading = false;
        // console.log(error);
        if (error.message === 'Network Error') {
          status.maschinenErrorMsg = 'Es konnt keine Verbindung zum Server hergestellt werden.';
        } else if (error.response?.status == 403) {
          status.maschinenErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.maschinenErrorMsg = error;
        }

        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.UpdateGetProduktionsstatus]({ commit }: ActionArguments) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;

    return LagerService.getProduktionsstatus()
      .then((res) => {
        status.produktionsstatusFetched = true;
        commonStore.setStatus(status);

        const result: Array<Produktionsstatus> = res.data;
        commit(MutationTypes.GetProduktionsstatusSuccess, result);
      })
      .catch((error) => {
        if (error.message === 'Network Error') {
          status.maschinenErrorMsg = 'Es konnt keine Verbindung zum Server hergestellt werden.';
        } else if (error.response?.status == 403) {
          status.maschinenErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.maschinenErrorMsg = error;
        }

        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.getMaschinenPlanungHeute]({ commit }: ActionArguments) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.maschinenstatusLoading = true;
    status.maschinenstatusErrorMsg = '';
    commonStore.setStatus(status);

    const maschineids = [1, 6, 8, 10, 12, 24, 25];

    try {
      const res = await LagerService.getProduktionsstatus();
      let result = new Array<Maschinenplanung>();
      for (let i = 0; i < res.data.length; i++) {
        if (maschineids.includes(res.data[i].maschinenid)) {
          const maschine: Maschinenplanung = res.data[i];

          try {
            const resp = await LagerService.getMaschinenPlanungHeuteListeartikel(maschine.maschinename);
            if (resp.data != undefined && resp.data != '') {
              maschine.planung = resp.data;
            } else {
              maschine.planung = undefined;
            }
          } catch (err: any) {
            // maschine.planung = new Array<PlanungMaschinenHeute>();
          }

          result.push(maschine);
        }
      }

      commit(MutationTypes.SetMaschinenplanungHeute, result);
      status.maschinenstatusLoading = false;
      commonStore.setStatus(status);
    } catch (error: any) {
      status.maschinenstatusLoading = false;
      if (error.message === 'Network Error') {
        status.maschinenstatusErrorMsg = 'Es konnt keine Verbindung zum Server hergestellt werden.';
      } else if (error.response?.status == 403) {
        status.maschinenstatusErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
      } else {
        status.maschinenstatusErrorMsg = error;
      }

      commonStore.setStatus(status);
    }

    return;
  },

  async [ActionTypes.updateMaschinenPlanungHeute]({ commit }) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.maschinenstatusErrorMsg = '';
    commonStore.setStatus(status);

    const maschineids = [1, 6, 8, 10, 12, 24, 25];

    try {
      const res = await LagerService.getProduktionsstatus();

      let result = new Array<Maschinenplanung>();
      for (let i = 0; i < res.data.length; i++) {
        if (maschineids.includes(res.data[i].maschinenid)) {
          const maschine: Maschinenplanung = res.data[i];

          try {
            const resp = await LagerService.getMaschinenPlanungHeuteListeartikel(maschine.maschinename);
            maschine.planung = resp.data;
          } catch (err: any) {
            // maschine.planung = new Array<PlanungMaschinenHeute>();
          }

          result.push(maschine);
        }
      }

      commit(MutationTypes.SetMaschinenplanungHeute, result);
    } catch (error: any) {
      if (error.message === 'Network Error') {
        status.maschinenstatusErrorMsg = 'Es konnt keine Verbindung zum Server hergestellt werden.';
      } else if (error.response?.status == 403) {
        status.maschinenstatusErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
      } else {
        status.maschinenstatusErrorMsg = error;
      }

      commonStore.setStatus(status);
    }
  },
};
